import { Inject, Injectable } from '@angular/core';
import { PULSE_FEATURE_NAME } from '@epicuro-next/constants/rule-engine/pulse-rules';
import { UsersApiService } from '@epicuro-next/data/users';
import {
  APP_TOP_LEVEL_PATH,
  PULSE_TOP_ROUTE_KEY,
} from '@epicuro-next/navigation/constants';
import {
  BroadcastService,
  EventType,
  LoginService,
} from '@epicuro-next/platform/authentication';
import {
  initConfigSuccess,
  NAMESPACE_NAVIGATION,
  NavigationConfigurationKeys,
  NavigationConfigurations,
  selectConfigurationValueFrom,
  selectShowWspDefaultValueRule,
} from '@epicuro-next/platform/configuration';
import { registerUserDefinition } from '@epicuro-next/platform/sentry';
import {
  BROWSER_STORAGE_SERVICE,
  BROWSER_STORAGE_TOKEN,
} from '@epicuro-next/platform/storage';
import {
  clearActiveAccount,
  setAccountBESearchEnabled,
} from '@epicuro-next/state/accounts-state';
import { go } from '@epicuro-next/state/router-store';
import { ACCOUNT_ID, selectAccountId } from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { distinctUntilChanged, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import {
  loadUser,
  loadUserFailure,
  loadUserSuccess,
  login,
  loginFailure,
  loginSuccess,
  logout,
  setShowWholesalePrices,
  setSubscribeToAllEmails,
  setSubscribeToAllEmailsSuccess,
} from '../actions';
import {
  selectIsAdminOrCustomerService,
  selectShowWholeSalePrices,
} from '../user-state.selectors';

@Injectable()
export class UserEffects {
  public onInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      map(() => loadUser()),
    ),
  );

  public loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUser),
      switchMap(() => {
        return this.userService.getCurrent().pipe(
          map((user) => loadUserSuccess({ user })),
          catchError(() => of(loadUserFailure({ error: 'User error' }))),
        );
      }),
    ),
  );

  public login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      switchMap(({ username, password }) =>
        this.loginService.login(username, password).pipe(
          switchMap(() => [
            loginSuccess(),
            go({
              commands: ['/'],
            }),
          ]),
          catchError((error) => {
            return of(
              loginFailure({ error: error?.error?.error ?? error.message }),
            );
          }),
        ),
      ),
    ),
  );

  public acquireTokenSuccess$ = createEffect(() =>
    this.authBroadcastService.broadcast$.pipe(
      filter((evt) => {
        return (
          typeof evt === 'object' &&
          evt.type === EventType.ACQUIRE_TOKEN_SUCCESS
        );
      }),
      map(() => loginSuccess()),
    ),
  );

  public logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        switchMap(() =>
          this.userService
            .logout()
            .pipe(
              tap(() =>
                this.authBroadcastService.emit({ type: EventType.USER_LOGOUT }),
              ),
            ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  public registerAsSentryUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserSuccess),
        tap(({ user }) => {
          registerUserDefinition(user.email, user.displayName);
        }),
      ),
    { dispatch: false },
  );

  public logoutCleanup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      tap(() => this.storageService.remove(ACCOUNT_ID)),
      map(() => clearActiveAccount()),
    ),
  );

  public setIsAccountBESearchEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserSuccess),
      concatLatestFrom(() =>
        this.store.pipe(select(selectIsAdminOrCustomerService)),
      ),
      map(([_, isAdminOrCustomerService]) =>
        setAccountBESearchEnabled({
          isBESearchEnabled: isAdminOrCustomerService,
        }),
      ),
    ),
  );

  public setIsSubscribedToAccountBaskets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSubscribeToAllEmails),
      switchMap(({ isSubscribedToAccountBaskets }) =>
        this.userService
          .setIsSubscribedToAccountBaskets(isSubscribedToAccountBaskets)
          .pipe(
            map(() =>
              setSubscribeToAllEmailsSuccess({ isSubscribedToAccountBaskets }),
            ),
            catchError(() => [
              showSnackBarMessage({
                message: $localize`:UserMenu@@failedChangeSubscribeToAllAccounts:There was an error while changing settings`,
                messageType: MessageType.DANGER,
              }),
            ]),
          ),
      ),
    ),
  );

  public disableShowWspByRule$ = createEffect(() =>
    this.store.pipe(
      select(selectAccountId),
      filter(isNonNullish),
      distinctUntilChanged(),
      switchMap(() =>
        this.actions$.pipe(
          ofType(initConfigSuccess),
          filter(({ configurations }) =>
            Object.keys(configurations).includes(PULSE_FEATURE_NAME),
          ),
          take(1),
        ),
      ),
      concatLatestFrom(() => [
        this.store.pipe(select(selectShowWspDefaultValueRule)),
        this.store.pipe(select(selectShowWholeSalePrices)),
      ]),
      filter(([_, defaultValue, userValue]) => !defaultValue && userValue),
      map(() =>
        setShowWholesalePrices({
          show: false,
        }),
      ),
    ),
  );

  public redirectToOffboard$ = createEffect(() =>
    this.store.pipe(
      select(selectAccountId),
      filter(isNonNullish),
      distinctUntilChanged(),
      switchMap(() =>
        this.actions$.pipe(
          ofType(initConfigSuccess),
          filter(({ configurations }) =>
            Object.keys(configurations).includes(NAMESPACE_NAVIGATION),
          ),
          take(1),
        ),
      ),
      concatLatestFrom(() =>
        this.store.pipe(
          select(
            selectConfigurationValueFrom<
              NavigationConfigurationKeys.OffboardingEnabled,
              NavigationConfigurations
            >(
              NAMESPACE_NAVIGATION,
              NavigationConfigurationKeys.OffboardingEnabled,
              false,
            ),
          ),
        ),
      ),
      filter(([_, isOffboardingEnabled]) => {
        return !!isOffboardingEnabled;
      }),
      map(() =>
        go({
          commands: [
            '/',
            `${APP_TOP_LEVEL_PATH[PULSE_TOP_ROUTE_KEY.OFFBOARDING].path}`,
          ],
        }),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private userService: UsersApiService,
    private authBroadcastService: BroadcastService,
    @Inject(BROWSER_STORAGE_TOKEN)
    private storageService: BROWSER_STORAGE_SERVICE,
    private store: Store,
    private loginService: LoginService,
  ) {}
}
